/** Typed fetch helpers — paths resolve against the Vite dev proxy (vite.config).
 *  Ported from builder-ui so studio-ui talks to the same backend the same way. */

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API ${status}`);
  }
}

/** ZITADEL access token, kept in module scope and refreshed by <TokenSync>.
 *
 *  The module value alone is not enough: on a hard reload the first render's
 *  React Query fetches can fire BEFORE <TokenSync>'s effect runs, so the request
 *  goes out without a Bearer. That was harmless while JWT_ENFORCE=false (the
 *  demo fallback answered) but returns 401 once enforcement is on. Fall back to
 *  a synchronous read of the persisted OIDC user so the very first request is
 *  already authenticated. */
let authToken: string | null = null;

/** react-oidc-context/oidc-client-ts persist the user under this key prefix when
 *  backed by WebStorageStateStore (see auth/oidc.tsx). */
const OIDC_USER_KEY_PREFIX = "oidc.user:";

function tokenFromStorage(): string | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(OIDC_USER_KEY_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { access_token?: string; expired?: boolean };
      if (parsed.access_token && !parsed.expired) return parsed.access_token;
    }
  } catch {
    /* ignore unreadable/malformed storage */
  }
  return null;
}

export function setAuthToken(token: string | null): void {
  authToken = token;
}
export function getAuthToken(): string | null {
  return authToken ?? tokenFromStorage();
}
export function authHeader(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function handle<T>(resp: Response): Promise<T> {
  if (!resp.ok) {
    const raw = await resp.text();
    let body: unknown = raw;
    try {
      body = JSON.parse(raw);
    } catch {
      /* keep raw */
    }
    throw new ApiError(resp.status, body);
  }
  if (resp.status === 204) return undefined as T;
  const text = await resp.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export async function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(path, {
    ...init,
    method: "GET",
    credentials: "include",
    headers: { ...authHeader(), ...(init?.headers ?? {}) },
  });
  return handle<T>(resp);
}

export async function apiPost<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
  const req: RequestInit = {
    ...init,
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...authHeader(), ...(init?.headers ?? {}) },
  };
  if (body !== undefined) req.body = JSON.stringify(body);
  return handle<T>(await fetch(path, req));
}

export async function apiPatch<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
  const resp = await fetch(path, {
    ...init,
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/merge-patch+json",
      ...authHeader(),
      ...(init?.headers ?? {}),
    },
    body: JSON.stringify(body),
  });
  return handle<T>(resp);
}

export async function apiDelete<T = void>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(path, {
    ...init,
    method: "DELETE",
    credentials: "include",
    headers: { ...authHeader(), ...(init?.headers ?? {}) },
  });
  return handle<T>(resp);
}
